import { Blocks, isEditing } from "@builder.io/sdk-react";
import classNames from "classnames";
import Image from "next/image";

import { Container } from "@jti/core-components/components/Container";
import type { HeroV2Content, HeroV2Props } from "@jti/ui/components/HeroV2";
import { HeroV2 } from "@jti/ui/components/HeroV2";

import { AuthoringWarningMessage } from "../AuthoringWarningMessage";

import { ResponsiveBackgroundImage } from "./ResponsiveBackgroundImage";
import { HeroDataSchema } from "./schema";

import styles from "./HeroBannerContainer.module.scss";

export const HERO_VALIDATION_MESSAGE =
  "Hero validation failed. Please ensure all required fields are filled: background type, mobile variant, and background images (desktop & mobile with src, title, alt) or video. If CTA is added, it must include: link, label, theme, layout, tracking, and target.";

function isValidHeroV2Block(block: BuilderBlock): block is BuilderBlock & {
  component: { name: string; options: { data: HeroV2Content } };
} {
  return !!block.component && !!block.component.options && typeof block.component.options.data === "object";
}

export type HeroContainerProps = {
  isContainerWidth: boolean;
  isViewportHeight: boolean;
  childBlocks: BuilderBlock[];
  builderBlock: BuilderBlock;
  autoplayEnabled?: boolean;
  autoplayDelay?: string;
  breadcrumbsSpacing?: boolean;
};

export function HeroBannerContainer({
  isContainerWidth,
  isViewportHeight,
  childBlocks,
  builderBlock,
  autoplayEnabled = false,
  autoplayDelay = "5",
  breadcrumbsSpacing = false,
}: HeroContainerProps) {
  const isChildBlocksArray = Array.isArray(childBlocks);

  const convertedData = isChildBlocksArray
    ? childBlocks.filter(isValidHeroV2Block).map((item, index): ArrayElement<HeroV2Props["data"]> | null => {
        const heroData = item?.component?.options.data;
        heroData.removeVideoRoundedCorners = true;

        let validatedHeroData;

        try {
          validatedHeroData = HeroDataSchema.parse(heroData);
        } catch (error) {
          console.error("Hero data validation failed", error);
          return null;
        }

        return {
          ...validatedHeroData,
          renderBackgroundImage: (props) => <ResponsiveBackgroundImage {...props} index={index} />,
          withBreadcrumbs: breadcrumbsSpacing,
          renderImage: ({ src }: { src: string }) => (
            <Image
              src={src}
              alt={heroData.logoAlt}
              height={heroData.logoHeight ?? 63}
              width={heroData.logoWidth ?? 100}
            />
          ),
        };
      })
    : [];

  const filteredConvertedData: HeroV2Content[] = convertedData.filter((data): data is HeroV2Content => data !== null);

  const hasValidationErrors = convertedData.length !== filteredConvertedData.length;
  const isSuccessfullyValidated = !hasValidationErrors && filteredConvertedData.length > 0;

  if (isEditing()) {
    return (
      <AuthoringWarningMessage isSuccessfullyValidated={isSuccessfullyValidated} message={HERO_VALIDATION_MESSAGE}>
        <Blocks parent={builderBlock?.id} blocks={childBlocks} className={styles.editingWrapper} />
      </AuthoringWarningMessage>
    );
  }

  const Wrapper = isContainerWidth ? Container : "div";

  return (
    <Wrapper
      className={classNames({
        [styles.fullWidthContainer as string]: !isContainerWidth,
        [styles.fullHeightContainer as string]: !isContainerWidth && isViewportHeight,
      })}
    >
      <HeroV2
        data={filteredConvertedData}
        isContainerWidth={isContainerWidth}
        isViewportHeight={isViewportHeight}
        autoplayEnabled={autoplayEnabled}
        autoplayDelayInSeconds={Number(autoplayDelay)}
        withLoader
      />
    </Wrapper>
  );
}
